// /opt/isp_erp/frontend/src/pages/admin/finanzas/promesas/PromesaForm.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../../../../api/axios';
import { promesasApi } from '../../../../api/finanzas/promesas.api';
import './PromesaForm.css';

export default function PromesaForm() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [tipoPromesa, setTipoPromesa] = useState('PAGO_UNICO');
  const [cuotas, setCuotas] = useState([]);
  const [facturasCliente, setFacturasCliente] = useState([]);
  const [formData, setFormData] = useState({
    customer_id: '',
    tipo: 'PAGO_UNICO',
    monto_total: '',
    fecha_limite: '',
    descripcion: '',
    condiciones: '',
    facturas_ids: []
  });

  // Cargar TODOS los clientes activos Y suspendidos al montar
  useEffect(() => {
    cargarClientes();
  }, []);

  // Filtrar clientes en memoria (sin requests adicionales)
  const clientesFiltrados = useMemo(() => {
    if (!searchTerm.trim()) return [];

    const term = searchTerm.toLowerCase().trim();
    return clientes.filter(c =>
      c.nombres?.toLowerCase().includes(term) ||
      c.apellidos?.toLowerCase().includes(term) ||
      c.identificacion?.includes(term)
    );
  }, [searchTerm, clientes]);

  // Cuando se selecciona un cliente, cargar sus facturas pendientes
  useEffect(() => {
    if (formData.customer_id) {
      cargarFacturasCliente(formData.customer_id);
    }
  }, [formData.customer_id]);

  const cargarClientes = async () => {
    try {
      // Cargar clientes ACTIVOS y SUSPENDIDOS
      const response = await axios.get('/customers/multiple-estados?estados=ACTIVO,SUSPENDIDO');
      setClientes(response.data);
    } catch (error) {
      console.error('Error cargando clientes:', error);
    }
  };

  const cargarFacturasCliente = async (customerId) => {
    try {
      const response = await axios.get(`/facturas?customer_id=${customerId}&estado=PENDIENTE,PARCIAL&limit=100`);
      setFacturasCliente(response.data);
    } catch (error) {
      console.error('Error cargando facturas:', error);
    }
  };

  const seleccionarCliente = (cliente) => {
    setFormData({ ...formData, customer_id: cliente.id });
    setSearchTerm('');
  };

  const handleTipoChange = (tipo) => {
    setTipoPromesa(tipo);
    setFormData({ ...formData, tipo });
    if (tipo === 'PAGO_UNICO') {
      setCuotas([]);
    }
  };

  const agregarCuota = () => {
    setCuotas([
      ...cuotas,
      {
        numero_cuota: cuotas.length + 1,
        monto: '',
        fecha_vencimiento: ''
      }
    ]);
  };

  const eliminarCuota = (index) => {
    const nuevasCuotas = cuotas.filter((_, i) => i !== index);
    // Reordenar números de cuota
    nuevasCuotas.forEach((c, i) => c.numero_cuota = i + 1);
    setCuotas(nuevasCuotas);
  };

  const actualizarCuota = (index, campo, valor) => {
    const nuevasCuotas = [...cuotas];
    nuevasCuotas[index][campo] = valor;
    setCuotas(nuevasCuotas);

    // Calcular monto total si es necesario
    if (campo === 'monto') {
      const total = nuevasCuotas.reduce((sum, c) => sum + (parseFloat(c.monto) || 0), 0);
      setFormData({ ...formData, monto_total: total });
    }
  };

  const handleFacturaToggle = (facturaId) => {
    const nuevasFacturas = formData.facturas_ids.includes(facturaId)
      ? formData.facturas_ids.filter(id => id !== facturaId)
      : [...formData.facturas_ids, facturaId];

    setFormData({ ...formData, facturas_ids: nuevasFacturas });
  };

  // 👇 FUNCIÓN PARA AJUSTAR LA FECHA Y EVITAR DESFASE HORARIO
  const ajustarFechaUTC = (fechaStr) => {
    if (!fechaStr) return null;
    // El input date devuelve YYYY-MM-DD en zona local
    // Lo enviamos tal cual para que el backend lo interprete como fecha local
    return fechaStr;
  };

  // ✅ HANDLESUBMIT CORREGIDO
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validaciones básicas
    if (!formData.customer_id) {
      alert('Seleccione un cliente');
      return;
    }

    if (formData.monto_total === null || formData.monto_total === undefined || formData.monto_total < 0) {
      alert('Ingrese un monto total válido');
      return;
    }

    if (!formData.fecha_limite) {
      alert('Ingrese una fecha límite');
      return;
    }

    if (tipoPromesa === 'CUOTAS' && cuotas.length === 0) {
      alert('Agregue al menos una cuota');
      return;
    }

    // Validar cuotas
    if (tipoPromesa === 'CUOTAS') {
      for (let i = 0; i < cuotas.length; i++) {
        const c = cuotas[i];
        if (!c.monto || c.monto <= 0) {
          alert(`La cuota ${i + 1} debe tener un monto válido`);
          return;
        }
        if (!c.fecha_vencimiento) {
          alert(`La cuota ${i + 1} debe tener fecha de vencimiento`);
          return;
        }
      }
    }

    setLoading(true);
    try {
      const payload = {
        customer_id: formData.customer_id,
        tipo: tipoPromesa,
        monto_total: parseFloat(formData.monto_total),
        fecha_limite: ajustarFechaUTC(formData.fecha_limite),
        descripcion: formData.descripcion || null,
        condiciones: formData.condiciones || null,
        facturas_ids: formData.facturas_ids.length > 0 ? formData.facturas_ids : null
      };

      if (tipoPromesa === 'CUOTAS') {
        payload.cuotas = cuotas.map(c => ({
          numero_cuota: c.numero_cuota,
          monto: parseFloat(c.monto),
          fecha_vencimiento: ajustarFechaUTC(c.fecha_vencimiento)
        }));
      }

      const response = await promesasApi.crear(payload);
      alert('✅ Promesa creada exitosamente');
      navigate(`/finanzas/promesas/${response.data.id}`);
    } catch (error) {
      console.error('Error creando promesa:', error);
      alert(error.response?.data?.detail || 'Error al crear la promesa');
    } finally {
      setLoading(false);
    }
  };

  const getEstadoClass = (estado) => {
    const classes = {
      ACTIVO: 'estado-activo',
      SUSPENDIDO: 'estado-suspendido',
      RETIRADO: 'estado-retirado'
    };
    return classes[estado] || '';
  };

  const formatearFechaParaInput = (fecha) => {
    if (!fecha) return '';
    return fecha;
  };

  return (
    <div className="promesa-form-container">
      {/* Header */}
      <div className="form-header">
        <div className="form-header-left">
          <div className="form-header-icon">📅</div>
          <div>
            <h1>Nueva Promesa de Pago</h1>
            <p>Complete los datos para crear una nueva promesa</p>
          </div>
        </div>
        <button
          onClick={() => navigate('/finanzas/promesas')}
          className="form-btn-cancel"
        >
          ← Cancelar
        </button>
      </div>

      <form onSubmit={handleSubmit} className="promesa-form">
        {/* Selección de cliente */}
        <div className="form-section">
          <h2>Información del Cliente</h2>

          <div className="form-group">
            <label className="required">
              Cliente <span className="required-star">*</span>
            </label>

            {!formData.customer_id ? (
              <>
                <div className="search-container">
                  <input
                    type="text"
                    placeholder="Buscar cliente por nombre o identificación..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="form-input search-input"
                    autoFocus
                  />
                  {searchTerm && (
                    <button
                      type="button"
                      onClick={() => setSearchTerm('')}
                      className="search-clear"
                    >
                      ✕
                    </button>
                  )}
                </div>

                {searchTerm && (
                  <div className="clientes-list">
                    {clientesFiltrados.length === 0 ? (
                      <div className="clientes-empty">
                        <p>No se encontraron clientes</p>
                      </div>
                    ) : (
                      clientesFiltrados.map(cliente => (
                        <div
                          key={cliente.id}
                          onClick={() => seleccionarCliente(cliente)}
                          className="cliente-item"
                        >
                          <div className="cliente-info">
                            <p className="cliente-nombre">
                              {cliente.nombres} {cliente.apellidos}
                            </p>
                            <p className="cliente-id">ID: {cliente.identificacion}</p>
                          </div>
                          <span className={`cliente-estado ${getEstadoClass(cliente.estado)}`}>
                            {cliente.estado}
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </>
            ) : (
              <div className="cliente-seleccionado">
                <div className="cliente-seleccionado-info">
                  <div className="cliente-avatar">
                    {clientes.find(c => c.id === formData.customer_id)?.nombres?.charAt(0) || '👤'}
                  </div>
                  <div>
                    <p className="cliente-seleccionado-nombre">
                      {clientes.find(c => c.id === formData.customer_id)?.nombres} {clientes.find(c => c.id === formData.customer_id)?.apellidos}
                    </p>
                    <p className="cliente-seleccionado-id">
                      ID: {clientes.find(c => c.id === formData.customer_id)?.identificacion}
                    </p>
                    <p className="cliente-seleccionado-estado">
                      Estado: <span className={getEstadoClass(clientes.find(c => c.id === formData.customer_id)?.estado)}>
                        {clientes.find(c => c.id === formData.customer_id)?.estado}
                      </span>
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, customer_id: '' })}
                  className="btn-cambiar-cliente"
                >
                  Cambiar
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Tipo de promesa */}
        <div className="form-section">
          <h2>Tipo de Promesa</h2>
          <div className="tipo-promesa-options">
            <label className={`tipo-option ${tipoPromesa === 'PAGO_UNICO' ? 'selected' : ''}`}>
              <input
                type="radio"
                name="tipo"
                checked={tipoPromesa === 'PAGO_UNICO'}
                onChange={() => handleTipoChange('PAGO_UNICO')}
                className="tipo-radio"
              />
              <span className="tipo-label">💰 Pago Único</span>
            </label>
            <label className={`tipo-option ${tipoPromesa === 'CUOTAS' ? 'selected' : ''}`}>
              <input
                type="radio"
                name="tipo"
                checked={tipoPromesa === 'CUOTAS'}
                onChange={() => handleTipoChange('CUOTAS')}
                className="tipo-radio"
              />
              <span className="tipo-label">📅 Pago en Cuotas</span>
            </label>
          </div>
        </div>

        {/* Monto total y fecha límite */}
        <div className="form-section">
          <h2>Detalles de la Promesa</h2>
          <div className="form-row">
            <div className="form-group">
              <label className="required">
                Monto Total ($) <span className="required-star">*</span>
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.monto_total}
                onChange={(e) => setFormData({...formData, monto_total: e.target.value})}
                className="form-input"
                placeholder="0.00 (0 = solo extensión de fecha)"
                required
                disabled={tipoPromesa === 'CUOTAS' && cuotas.length > 0}
              />
              <small className="form-hint">
                 💡 Si el monto es 0, solo se extiende la fecha de corte sin compromiso de pago
              </small>
            </div>
            <div className="form-group">
              <label className="required">
                Fecha Límite <span className="required-star">*</span>
              </label>
              <input
                type="date"
                value={formData.fecha_limite}
                onChange={(e) => setFormData({...formData, fecha_limite: e.target.value})}
                className="form-input"
                min={new Date().toISOString().split('T')[0]}
                required
              />
            </div>
          </div>
        </div>

        {/* Cuotas (solo si es CUOTAS) */}
        {tipoPromesa === 'CUOTAS' && (
          <div className="form-section">
            <div className="section-header">
              <h2>Cuotas</h2>
              <button
                type="button"
                onClick={agregarCuota}
                className="btn-agregar-cuota"
              >
                ➕ Agregar Cuota
              </button>
            </div>

            {cuotas.length === 0 ? (
              <div className="cuotas-empty">
                <p>Agregue al menos una cuota</p>
              </div>
            ) : (
              <div className="cuotas-list">
                {cuotas.map((cuota, index) => (
                  <div key={index} className="cuota-item">
                    <div className="cuota-header">
                      <span className="cuota-numero">Cuota #{cuota.numero_cuota}</span>
                      <button
                        type="button"
                        onClick={() => eliminarCuota(index)}
                        className="btn-eliminar-cuota"
                      >
                        Eliminar
                      </button>
                    </div>
                    <div className="cuota-grid">
                      <div className="cuota-field">
                        <label className="cuota-label">Monto ($)</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0.01"
                          value={cuota.monto}
                          onChange={(e) => actualizarCuota(index, 'monto', e.target.value)}
                          className="cuota-input"
                          placeholder="0.00"
                          required
                        />
                      </div>
                      <div className="cuota-field">
                        <label className="cuota-label">Vencimiento</label>
                        <input
                          type="date"
                          value={formatearFechaParaInput(cuota.fecha_vencimiento)}
                          onChange={(e) => actualizarCuota(index, 'fecha_vencimiento', e.target.value)}
                          className="cuota-input"
                          min={new Date().toISOString().split('T')[0]}
                          required
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Facturas asociadas (opcional) */}
        {formData.customer_id && facturasCliente.length > 0 && (
          <div className="form-section">
            <h2>Facturas a incluir (opcional)</h2>
            <div className="facturas-list">
              {facturasCliente.map(factura => (
                <label key={factura.id} className="factura-item">
                  <input
                    type="checkbox"
                    checked={formData.facturas_ids.includes(factura.id)}
                    onChange={() => handleFacturaToggle(factura.id)}
                    className="factura-checkbox"
                  />
                  <span className="factura-info">
                    <span className="factura-numero">{factura.numero_factura}</span>
                    <span className="factura-monto">${factura.saldo_pendiente?.toFixed(2)}</span>
                    <span className="factura-vencimiento">
                      Vence: {new Date(factura.fecha_vencimiento).toLocaleDateString()}
                    </span>
                  </span>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Descripción y condiciones */}
        <div className="form-section">
          <h2>Información Adicional</h2>
          <div className="form-group">
            <label>Descripción</label>
            <textarea
              value={formData.descripcion}
              onChange={(e) => setFormData({...formData, descripcion: e.target.value})}
              className="form-textarea"
              rows="2"
              placeholder="Motivo de la promesa..."
            />
          </div>
          <div className="form-group">
            <label>Condiciones</label>
            <textarea
              value={formData.condiciones}
              onChange={(e) => setFormData({...formData, condiciones: e.target.value})}
              className="form-textarea"
              rows="2"
              placeholder="Condiciones del acuerdo..."
            />
          </div>
        </div>

        {/* Botones de acción */}
        <div className="form-actions">
          <button
            type="button"
            onClick={() => navigate('/finanzas/promesas')}
            className="btn-secondary"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={loading}
            className="btn-primary"
          >
            {loading ? (
              <>
                <span className="loading-spinner-small"></span>
                Creando...
              </>
            ) : (
              '✅ Crear Promesa'
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
